import type { CSSProperties } from 'react';

/**
 * Returns scoped CSS custom-property overrides that scale the typography,
 * spacing, radius, and icon-size tokens by `scale`. Meant to be spread onto
 * a `style` prop on a surface root (e.g. DataTable) so every descendant that
 * reads these tokens — chips, header, toolbar, row-action icons, cell text —
 * scales together instead of only the element the fontSize was set on.
 *
 * The rem literals below MIRROR `src/renderer/base.css` and MUST move
 * together with it — if a base.css token value changes, update the matching
 * literal here.
 *
 * At scale === 1 this returns `{}` so 100% stays byte-identical to the
 * unscaled app (no inline overrides emitted, base.css values apply as-is).
 */
export function scaledTokens(scale: number): CSSProperties {
  if (scale === 1) return {};

  // --sf-u feeds rem() (src/renderer/lib/rem.ts) and the Tailwind spacing
  // scale (tailwind.config.js) so every rem-authored size and utility class
  // under this surface root scales with the slider too, not just tokens.

  const font = {
    xs: 0.625,
    sm: 0.6875,
    base: 0.8125,
    md: 0.875,
    lg: 1,
    xl: 1.125,
    '2xl': 1.375,
    '3xl': 1.75,
  };
  const space = {
    1: 0.25,
    2: 0.5,
    3: 0.75,
    4: 1,
    5: 1.5,
    6: 2,
    7: 3,
    8: 4,
  };
  const radius = {
    sm: 0.25,
    md: 0.375,
    lg: 0.625,
    xl: 1,
  };
  const iconSize = 1.125;

  const vars: Record<string, string> = {};
  for (const [key, base] of Object.entries(font)) {
    vars[`--font-size-${key}`] = `calc(${base}rem * ${scale})`;
  }
  for (const [key, base] of Object.entries(space)) {
    vars[`--space-${key}`] = `calc(${base}rem * ${scale})`;
  }
  for (const [key, base] of Object.entries(radius)) {
    vars[`--radius-${key}`] = `calc(${base}rem * ${scale})`;
  }
  vars['--icon-size'] = `calc(${iconSize}rem * ${scale})`;
  vars['--sf-u'] = String(scale);

  return vars as CSSProperties;
}
